import { ShieldCheck } from 'lucide-react';

import { perfilAPedir } from '@/lib/perfil-da-licenca';

import { CabecalhoPagina } from './CabecalhoPagina';
import { EstadoVazio } from './EstadoVazio';

/**
 * O quinto estado da tela (p.34) na mesa: sem permissão diz o perfil a pedir.
 *
 * Quem não tem a licença de uma ação não recebe o botão; quando a tela INTEIRA
 * é a ação (o cadastro de equipamento, a importação, o formulário em página),
 * ela recebe este estado no lugar do formulário — e não um formulário que só o
 * envio recusaria (prova do marco do ciclo 1, 07/10/2026).
 *
 * As mesmas palavras do celular (`src/mobile/componentes/Estados.tsx`):
 * `modulo` vem com o artigo ("o cadastro de equipamentos") e entra depois de
 * "libera", que concorda com qualquer gênero e número; `perfil` é um dos nove
 * papéis da proposta (o teste `src/mobile/perfis.test.ts` reprova o inventado).
 * O tom é o de atenção, como a p.32 desenha o sem permissão.
 */
export function SemPermissao({
  modulo,
  perfil,
  titulo = 'Você não tem acesso a esta tela',
}: {
  readonly modulo: string;
  readonly perfil: string;
  readonly titulo?: string;
}) {
  return (
    <EstadoVazio
      icone={ShieldCheck}
      tom="atencao"
      titulo={titulo}
      descricao={`Peça ao administrador da sua empresa o perfil ${perfil}: é ele que libera ${modulo}.`}
    />
  );
}

/**
 * A PÁGINA inteira sem permissão: o cabeçalho da tela e o estado, e nenhum
 * número ao lado — o "0 de 0 tarefas" que a manutenção mostrava ao operador
 * era um número que ele não pode ter, escrito como se fosse a fila.
 */
export function PaginaSemPermissao({
  sobretitulo,
  titulo,
  modulo,
  licenca,
}: {
  readonly sobretitulo: string;
  readonly titulo: string;
  readonly modulo: string;
  /** A licença que a tela pede; o perfil a pedir sai dela (`perfilAPedir`). */
  readonly licenca: string;
}) {
  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina sobretitulo={sobretitulo} titulo={titulo} />
        <SemPermissao modulo={modulo} perfil={perfilAPedir(licenca)} />
      </div>
    </div>
  );
}

/**
 * A PÁGINA DA EMPRESA INTEIRA aberta por quem tem acesso a só parte dela (uma
 * filial, uma obra ou um contrato) — robôs, rodada 3. É o mesmo quinto estado
 * (p.34), com a frase do servidor (`acesso/alcance.ts`), e nenhuma consulta: o
 * servidor recusaria todas, e cada recusa virava um toast "Não foi feito".
 */
export function PaginaForaDoAlcance({
  sobretitulo,
  titulo,
  frase,
}: {
  readonly sobretitulo: string;
  readonly titulo: string;
  readonly frase: string;
}) {
  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina sobretitulo={sobretitulo} titulo={titulo} />
        <EstadoVazio icone={ShieldCheck} tom="atencao" titulo="Esta tela é da empresa inteira" descricao={frase} />
      </div>
    </div>
  );
}
